import {
  API_ROUTES,
  organizationSchema,
  sessionSchema,
  type Organization,
  type Session,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { API_BASE_URL, createApiClient } from './client.js';
import { getAccessToken } from '../auth/supabase.js';

/**
 * The application's query surface.
 *
 * Query keys and fetchers are defined together, in one place, so that a cache
 * invalidation cannot drift from the request it is meant to invalidate. Every
 * feature imports from here rather than assembling its own key array.
 */

export const apiClient = createApiClient({
  baseUrl: API_BASE_URL,
  getAccessToken,
});

export const queryKeys = {
  session: ['session'] as const,
  organizations: {
    all: ['organizations'] as const,
    detail: (organizationId: string) => ['organizations', organizationId] as const,
  },
} as const;

/**
 * Which client this browser is looking at. UI state, not business state: the
 * server authorizes every request against the organization named in its URL,
 * so this preference can never widen access, only choose among memberships.
 */
const ACTIVE_ORG_KEY = 'hvac.activeOrganizationId';

export function setActiveOrganization(organizationId: string): void {
  localStorage.setItem(ACTIVE_ORG_KEY, organizationId);
}

export const sessionQuery = () =>
  queryOptions({
    queryKey: queryKeys.session,
    queryFn: async ({ signal }): Promise<Session> => {
      const session = await apiClient.request({
        path: API_ROUTES.session.current,
        schema: sessionSchema,
        signal,
      });
      const preferred = localStorage.getItem(ACTIVE_ORG_KEY);
      const isMember =
        preferred !== null &&
        session.organizations.some((entry) => entry.organization.id === preferred);
      return isMember ? { ...session, activeOrganizationId: preferred } : session;
    },
    // The session drives navigation and permissions. Re-fetching it on every
    // focus is cheap and keeps a revoked role from lingering in a stale tab.
    staleTime: 30_000,
  });

export async function createOrganization(request: {
  name: string;
  province: string;
}): Promise<Organization> {
  return apiClient.request({
    path: API_ROUTES.organizations.create,
    method: 'POST',
    body: request,
    schema: organizationSchema,
  });
}

export const organizationsQuery = () =>
  queryOptions({
    queryKey: queryKeys.organizations.all,
    queryFn: ({ signal }): Promise<Organization[]> =>
      apiClient.request({
        path: API_ROUTES.organizations.list,
        schema: z.array(organizationSchema),
        signal,
      }),
  });

/**
 * Tells the server a sign-in just succeeded, so each business's trail records
 * it. Never allowed to stand between a person and the app: if it fails, the
 * sign-in still stands.
 */
export async function recordSignIn(): Promise<void> {
  try {
    await apiClient.request({
      path: API_ROUTES.session.signedIn,
      method: 'POST',
      body: {},
      schema: z.object({ recorded: z.number() }),
    });
  } catch {
    // The trail misses one entry; the person is not locked out over it.
  }
}
